/**
 * StoreOverlayContext - Open the mobile store-item slide-in from the Store grid
 * without changing the URL (same idea as PostOverlayContext).
 *
 * AppLayout owns state and renders `<StoreItemOverlay />`. Consumers call
 * `openStoreItem(itemId)`; `isMobile` lets callers keep `navigate('/store/:id')`
 * on desktop/tablet.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';

const StoreOverlayContext = createContext(null);

const MOBILE_QUERY = '(max-width: 767px)';

function readIsMobile() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  const mql = window.matchMedia(MOBILE_QUERY);
  return Boolean(mql && mql.matches);
}

export function StoreOverlayProvider({ children, openStoreItem, closeStoreItem, itemId }) {
  const [isMobile, setIsMobile] = useState(readIsMobile);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(MOBILE_QUERY);
    if (!mql) return undefined;
    const onChange = (e) => setIsMobile(Boolean(e?.matches));
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    if (mql.addListener) {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
    return undefined;
  }, []);

  const value = { isMobile, itemId, openStoreItem, closeStoreItem };
  return <StoreOverlayContext.Provider value={value}>{children}</StoreOverlayContext.Provider>;
}

export function useStoreOverlay() {
  return (
    useContext(StoreOverlayContext) || {
      isMobile: false,
      itemId: null,
      openStoreItem: () => {},
      closeStoreItem: () => {},
    }
  );
}

export default StoreOverlayContext;
